import axios, { AxiosResponse } from 'axios'

export const ACTIVE_TENANT_SESSION_KEY = 'dino_active_tenant_code'
export const FAILED_TENANT_SESSIONS_KEY = 'dino_failed_tenant_codes'
export const TENANT_SESSION_HEADER = 'X-Dino-Tenant-Code'
export const TENANT_SESSION_PARAMETER = 'dino_session'
export const TENANT_SESSION_RECOVERY_ERROR_CODE = 'TENANT_SESSION_RECOVERY'

export const createTenantSessionRecoveryError = () => {
    const error: any = new Error('Tenant session recovery in progress')
    error.code = TENANT_SESSION_RECOVERY_ERROR_CODE
    return error
}

export const isTenantSessionRecoveryError = (error: any) =>
    !!error && error.code === TENANT_SESSION_RECOVERY_ERROR_CODE

const TENANT_CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/
const SESSION_API_BASE = `${process.env.NODE_ENV === 'development' ? 'http://www-test.hadutech.net' : ''}`
    + '/creditx-admin/creditx-admin/v1/login-sessions'
const sessionClient = axios.create()
let recoveringTenantSession = false

export interface LoginSession {
    tenantId: number
    tenantCode: string
    tenantName: string
    userName: string
    displayName: string
    active: boolean
}

interface LoginSessionCandidate {
    tenantCode: string
}

const isValidTenantSession = (tenantCode: unknown): tenantCode is string =>
    typeof tenantCode === 'string' && TENANT_CODE_PATTERN.test(tenantCode)

export const getActiveTenantSession = () => {
    const tenantCode = sessionStorage.getItem(ACTIVE_TENANT_SESSION_KEY)
    if (!tenantCode) return null
    if (!TENANT_CODE_PATTERN.test(tenantCode)) {
        sessionStorage.removeItem(ACTIVE_TENANT_SESSION_KEY)
        return null
    }
    return tenantCode
}

export const setActiveTenantSession = (tenantCode: string) => {
    if (!TENANT_CODE_PATTERN.test(tenantCode)) throw new Error('Invalid tenant session code')
    sessionStorage.setItem(ACTIVE_TENANT_SESSION_KEY, tenantCode)
}

export const getFailedTenantSessions = (): string[] => {
    try {
        const storedValue = sessionStorage.getItem(FAILED_TENANT_SESSIONS_KEY)
        const tenantCodes = storedValue ? JSON.parse(storedValue) : []
        if (!Array.isArray(tenantCodes)) throw new Error('Invalid failed tenant sessions')
        return tenantCodes.filter((tenantCode, index) =>
            isValidTenantSession(tenantCode) && tenantCodes.indexOf(tenantCode) === index)
    } catch (error) {
        sessionStorage.removeItem(FAILED_TENANT_SESSIONS_KEY)
        return []
    }
}

const rememberFailedTenantSession = (tenantCode: string | null) => {
    if (!isValidTenantSession(tenantCode)) return
    const tenantCodes = getFailedTenantSessions()
    if (tenantCodes.indexOf(tenantCode) < 0) {
        tenantCodes.push(tenantCode)
        sessionStorage.setItem(FAILED_TENANT_SESSIONS_KEY, JSON.stringify(tenantCodes))
    }
}

const clearFailedTenantSessions = () =>
    sessionStorage.removeItem(FAILED_TENANT_SESSIONS_KEY)

const replaceCurrentUrl = (url: string) =>
    history.replaceState(history.state, '', url)

export const initializeActiveTenantSessionFromUrl = (
    url: string = location.href,
    replaceUrl: (url: string) => void = replaceCurrentUrl): string | null => {
    let currentUrl: URL
    try {
        currentUrl = new URL(url, location.origin)
    } catch (error) {
        return getActiveTenantSession()
    }
    const tenantCode = currentUrl.searchParams.get(TENANT_SESSION_PARAMETER)
    if (tenantCode === null) {
        return getActiveTenantSession()
    }
    currentUrl.searchParams.delete(TENANT_SESSION_PARAMETER)
    replaceUrl(`${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`)
    if (!isValidTenantSession(tenantCode)) {
        return getActiveTenantSession()
    }
    setActiveTenantSession(tenantCode)
    clearFailedTenantSessions()
    return tenantCode
}

export const initializeActiveTenantSession = (
    tenantCode: string | null | undefined): string | null => {
    const activeTenantCode = getActiveTenantSession()
    if (activeTenantCode) {
        clearFailedTenantSessions()
        return activeTenantCode
    }
    if (!tenantCode || !TENANT_CODE_PATTERN.test(tenantCode)) return null
    setActiveTenantSession(tenantCode)
    clearFailedTenantSessions()
    return tenantCode
}

export const clearActiveTenantSession = () => sessionStorage.removeItem(ACTIVE_TENANT_SESSION_KEY)

export const applyTenantSessionHeader = (headers: { [key: string]: any } = {}) => {
    const tenantCode = getActiveTenantSession()
    return tenantCode ? { ...headers, [TENANT_SESSION_HEADER]: tenantCode } : { ...headers }
}

export const withActiveTenantSession = (url: string) => {
    const tenantCode = getActiveTenantSession()
    if (!tenantCode) return url
    const hashIndex = url.indexOf('#')
    const baseUrl = hashIndex >= 0 ? url.substring(0, hashIndex) : url
    const hash = hashIndex >= 0 ? url.substring(hashIndex) : ''
    const separator = baseUrl.indexOf('?') >= 0 ? '&' : '?'
    return `${baseUrl}${separator}${TENANT_SESSION_PARAMETER}=${encodeURIComponent(tenantCode)}${hash}`
}

const unwrap = <T>(response: AxiosResponse): T => {
    const body = response && response.data
    if (!body || String(body.code) !== '0') {
        throw new Error((body && body.msg) || 'Failed to load login sessions')
    }
    return body.data as T
}

export const loadLoginSessions = (includeActiveSession = true) => sessionClient.post(SESSION_API_BASE, null, {
    withCredentials: true,
    headers: includeActiveSession ? applyTenantSessionHeader() : {}
}).then(response => unwrap<LoginSession[]>(response))

export const loadAddAccountUrl = (includeActiveSession = true) => sessionClient.post(`${SESSION_API_BASE}/login-url`, null, {
    withCredentials: true,
    headers: includeActiveSession ? applyTenantSessionHeader() : {}
}).then(response => unwrap<string>(response))

export const selectNextTenantSession = <T extends LoginSessionCandidate>(
    sessions: T[] = [], failedTenantCodes: string[] = []): T | null =>
    sessions.find(session => isValidTenantSession(session.tenantCode)
        && failedTenantCodes.indexOf(session.tenantCode) < 0) || null

const redirectToLogin = (loginUrl?: string) => {
    clearActiveTenantSession()
    clearFailedTenantSessions()
    if (loginUrl) {
        location.href = loginUrl
        return
    }
    loadAddAccountUrl(false)
        .then(url => { if (url) location.href = url })
        .catch(() => recoveringTenantSession = false)
}

export const recoverTenantSession = (loginUrl?: string) => {
    if (recoveringTenantSession) return
    recoveringTenantSession = true
    rememberFailedTenantSession(getActiveTenantSession())
    loadLoginSessions(false).then(sessions => {
        const nextSession = selectNextTenantSession(sessions, getFailedTenantSessions())
        if (!nextSession) {
            redirectToLogin(loginUrl)
            return
        }
        setActiveTenantSession(nextSession.tenantCode)
        location.reload()
    }).catch(() => redirectToLogin(loginUrl))
}
